import { useQueryClient } from '@tanstack/react-query'
import { useAblyChannel } from './useAblyChannel.js'

/**
 * useRealtimeSync — subscribes to the shared Ably channel and invalidates
 * React Query caches when another client publishes a change.
 *
 * Pattern: "signal, don't ship". Mutations publish a tiny event ("goals-changed"
 * or "meta-changed"). Other clients receive it and refetch authoritative data
 * from the KV store. This keeps payloads small, avoids CRDT complexity, and
 * keeps the server as the source of truth.
 *
 * Why the delay:
 *   Cloudflare Workers KV is eventually consistent — a GET immediately after
 *   a PUT can return stale data for up to ~60 seconds. We delay the
 *   invalidate by 1.5 seconds so by the time our refetch fires, the KV
 *   write has almost always propagated. The trade-off is minor: cross-tab
 *   sync takes ~2 seconds instead of ~300ms, which is still far better
 *   than the old 15-second polling.
 */
const PROPAGATION_DELAY_MS = 1500

export function useRealtimeSync() {
  const queryClient = useQueryClient()

  useAblyChannel('goals-changed', () => {
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['goals'] })
    }, PROPAGATION_DELAY_MS)
  })

  useAblyChannel('meta-changed', () => {
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['meta'] })
    }, PROPAGATION_DELAY_MS)
  })
}
